import { Component, OnInit, OnDestroy, inject, ChangeDetectorRef, NgZone, HostListener } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ToastService, ToastMessage } from '../../services/toast.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-toast',
  standalone: true,
  imports: [CommonModule],
  template: `
    <div class="toast-container" role="region" aria-label="Notifications">
      <div
        *ngFor="let toast of toasts"
        class="toast"
        [class.toast-success]="toast.variant === 'success'"
        [class.toast-error]="toast.variant === 'error'"
        [class.toast-info]="toast.variant === 'info'"
      >
        <div class="toast-content">
          <div class="toast-title">{{ toast.title }}</div>
          <div *ngIf="toast.description" class="toast-description">{{ toast.description }}</div>
        </div>
        <button
          type="button"
          class="toast-close"
          (click)="removeToast(toast.id, $event)"
          aria-label="Close notification"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <line x1="18" y1="6" x2="6" y2="18"></line>
            <line x1="6" y1="6" x2="18" y2="18"></line>
          </svg>
        </button>
      </div>
    </div>
  `,
  styles: [`
    .toast-container {
      position: fixed;
      top: 1rem;
      right: 1.25rem;
      z-index: 99999;
      padding: 0;
      display: flex;
      flex-direction: column;
      gap: 0.5rem;
      max-width: 520px;
      pointer-events: none;
    }

    .toast {
      pointer-events: auto;
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 1.25rem 1.5rem;
      border-radius: 0.75rem;
      box-shadow: 0 12px 20px -4px rgba(0, 0, 0, 0.12), 0 6px 8px -3px rgba(0, 0, 0, 0.07);
      background: white;
      border: 1px solid #e5e7eb;
      animation: slideIn 0.25s ease-out;
      min-width: 420px;
    }

    @keyframes slideIn {
      from {
        transform: translateY(-50%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }

    .toast-success {
      border-color: #10b981;
      background: #f0fdf4;
    }

    .toast-error {
      border-color: #ef4444;
      background: #fef2f2;
    }

    .toast-info {
      border-color: #3b82f6;
      background: #eff6ff;
    }

    .toast-content {
      flex: 1;
      min-width: 0;
    }

    .toast-title {
      font-weight: 600;
      font-size: 1.0625rem;
      color: #111827;
      line-height: 1.35;
    }

    .toast-description {
      font-size: 0.9375rem;
      color: #6b7280;
      margin-top: 0.375rem;
      line-height: 1.45;
    }

    .toast-close {
      background: transparent;
      border: none;
      color: #6b7280;
      cursor: pointer;
      padding: 0;
      margin-left: 0.75rem;
      width: 1.75rem;
      height: 1.75rem;
      display: flex;
      align-items: center;
      justify-content: center;
      border-radius: 0.375rem;
      transition: background-color 0.15s, color 0.15s;
      flex-shrink: 0;
    }

    .toast-close:hover {
      background-color: rgba(0, 0, 0, 0.08);
      color: #111827;
    }
  `]
})
export class ToastComponent implements OnInit, OnDestroy {
  toasts: ToastMessage[] = [];
  private subscription?: Subscription;
  private cdr = inject(ChangeDetectorRef);
  private ngZone = inject(NgZone);
  private toastService = inject(ToastService);

  @HostListener('document:keydown.escape')
  onEscape(): void {
    if (this.toasts.length > 0) {
      this.toastService.clear();
      this.cdr.markForCheck();
    }
  }

  ngOnInit(): void {
    this.subscription = this.toastService.toasts$.subscribe(toasts => {
      this.ngZone.run(() => {
        this.toasts = [...toasts];
        this.cdr.markForCheck();
      });
    });
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
  }

  removeToast(id: string, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    this.toastService.remove(id);
    this.cdr.markForCheck();
  }
}
